<template>
  <div class="aircraft-report-cameras-dialog" :aria-busy="isBusy">
    <div class="input-row buttons-group buttons-group--align-center">
      <CamsButton
        priority="primary"
        type="button"
        size="m"
        :disabled="isBusy"
        @click="confirmSelection"
      >
        {{ $t('saveSelectedCameras') }} ({{ selectedEntitiesForMultiEdit.length }})
      </CamsButton>
      <CamsButton type="button" size="m" :disabled="isBusy || !selectedEntitiesForMultiEdit.length" @click="clearSelection">
        {{ $t('clearCameraSelection') }}
      </CamsButton>
      <CamsButton priority="secondary" type="button" size="m" @click="closeDialog()">
        {{ $t('cancel') }}
      </CamsButton>
    </div>
    <SmartInputText
      v-model="searchText"
      class="input-row"
      :placeholder="$t('searchPlaceholder')"
      :disabled="isBusy"
      default-if-empty=""
      @keyup.native.enter="applyTableSettings()"
    >
      <CamsButton position="r" type="button" :disabled="isBusy" @click="applyTableSettings()">
        {{ $t('search') }}
      </CamsButton>
    </SmartInputText>
    <SpinnerLoading v-if="isBusy" class="loader_center" color="blue" />
    <div v-else-if="hasLoadError" role="alert">
      <p>{{ $t('loadAircraftReportCamerasError') }}</p>
      <CamsButton type="button" @click="loadPage">
        {{ $t('retry') }}
      </CamsButton>
    </div>
    <PacsTable
      v-else
      :key="selectionVersion"
      :column-captions="columnCaptions"
      :column-names="columnNames"
      :initial-selected-keys="selectedPageCameraNumbers"
      :data-rows="dataRows"
      :page-count="pageCount"
      :key-field="keyField"
      :sort-info="sortInfo"
      :total-count="totalCount"
      :current-page="currentPage"
      @new-page="selectPage"
      @new-sort="changeSort"
      @new-entities-for-multi-edit="updatePageSelection"
    />
  </div>
</template>

<script>
import PacsTable from "@/components/pacs/PacsTable.vue";
import {entityTableMixin, methodsForDialogMixin} from "@/components/pacs/mixins.js";
import {ACTION_LOAD_AIRCRAFT_ANALYTICS, CAMERA_AIRCRAFT_ANALYTICS_STRUCT} from "@/store/analytics/index.js";
import {ACTION_LOAD_CAMERAS, FIELDS_CAMERA} from "@/store/cameras/index.js";
import {PAGE_SIZES} from "@/utils/consts.js";

const CAMERA_NUMBER_FIELD = CAMERA_AIRCRAFT_ANALYTICS_STRUCT.fields.camera_number.name;

function normalizeCameraNumbers(cameraNumbers) {
  return [...new Set(cameraNumbers
    .filter(number => number !== null && number !== undefined && number !== "")
    .map(String))];
}

/** Выбор камер с аналитикой ЛА для отчёта с сохранением отметок между страницами. */
export default {
  name: "AircraftReportCamerasDialog",
  components: {PacsTable},
  mixins: [entityTableMixin, methodsForDialogMixin],
  props: {
    initialSelectedCameraNumbers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      entityStruct: CAMERA_AIRCRAFT_ANALYTICS_STRUCT,
      entityFields: [
        {name: CAMERA_NUMBER_FIELD, title: "cameraNumber", type: "string", sort: true},
        {name: "camera_title", title: "tableHeaders.name", type: "string"},
        {name: "camera_address", title: "cameraAddress", type: "string"},
      ],
      disableRouteSync: true,
      selectedEntitiesForMultiEdit: normalizeCameraNumbers(this.initialSelectedCameraNumbers),
      appliedSearchText: "",
      isSelectingAll: false,
      hasLoadError: false,
      selectionVersion: 0,
      requestVersion: 0,
    };
  },
  computed: {
    isBusy() {
      return this.isLoadingTable || this.isSelectingAll;
    },
    selectedPageCameraNumbers() {
      const selected = new Set(this.selectedEntitiesForMultiEdit);
      return this.dataRows.map(row => row._key_).filter(number => selected.has(number));
    },
  },
  methods: {
    confirmSelection() {
      if (this.isBusy) return;
      this.closeDialog({selectedCameraNumbers: [...this.selectedEntitiesForMultiEdit]});
    },
    clearSelection() {
      this.selectedEntitiesForMultiEdit = [];
      this.selectionVersion += 1;
    },
    updatePageSelection(selection) {
      if (selection === true) {
        this.selectAllCameras();
        return;
      }
      const pageNumbers = new Set(this.dataRows.map(row => row._key_));
      // Чекбокс заголовка меняет выбор текущей страницы; отметки на остальных сохраняем.
      this.selectedEntitiesForMultiEdit = normalizeCameraNumbers([
        ...this.selectedEntitiesForMultiEdit.filter(number => !pageNumbers.has(number)),
        ...selection,
      ]);
    },
    async selectAllCameras() {
      if (this.isBusy) return;
      this.isSelectingAll = true;
      this.hasLoadError = false;
      try {
        const cameraNumbers = [];
        // Получаем явные номера всех найденных камер, чтобы затем можно было снять отдельные отметки.
        for (let page = 1, pageCount = 1; page <= pageCount; page++) {
          const response = await this.$store.dispatch(`analytics/${ACTION_LOAD_AIRCRAFT_ANALYTICS}`, {
            fields: [CAMERA_NUMBER_FIELD],
            page,
            pageSize: PAGE_SIZES.DEFAULT,
            search: this.appliedSearchText,
            orderBy: this.sortInfoForApi,
            filters: [],
          });
          if (this._isDestroyed || this._isBeingDestroyed) return;
          cameraNumbers.push(...response.results.map(camera => camera[CAMERA_NUMBER_FIELD]));
          pageCount = response.page.all;
        }
        this.selectedEntitiesForMultiEdit = normalizeCameraNumbers([
          ...this.selectedEntitiesForMultiEdit,
          ...cameraNumbers,
        ]);
      } catch {
        this.hasLoadError = true;
      } finally {
        this.isSelectingAll = false;
      }
    },
    async loadPage() {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      // Ответ предыдущего поиска или страницы не должен заменять актуальную таблицу.
      const version = ++this.requestVersion;
      const search = this.searchText;
      this.isLoadingTable = true;
      this.hasLoadError = false;
      try {
        const response = await this.$store.dispatch(`analytics/${ACTION_LOAD_AIRCRAFT_ANALYTICS}`, {
          fields: [CAMERA_NUMBER_FIELD],
          page: this.currentPage,
          pageSize: 10,
          search,
          orderBy: this.sortInfoForApi,
          filters: [],
        });
        if (version !== this.requestVersion || this._isDestroyed || this._isBeingDestroyed) return;
        const cameraNumbers = normalizeCameraNumbers(response.results.map(row => row[CAMERA_NUMBER_FIELD]));
        const cameras = cameraNumbers.length ? await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
          numbers: cameraNumbers,
          fields: [FIELDS_CAMERA.number, FIELDS_CAMERA.title, FIELDS_CAMERA.address],
        }) : [];
        if (version !== this.requestVersion || this._isDestroyed || this._isBeingDestroyed) return;
        const camerasByNumber = new Map(cameras.map(camera => [String(camera[FIELDS_CAMERA.number]), camera]));
        this.pageCount = response.page.all;
        this.totalCount = response.count;
        this.appliedSearchText = search;
        this.dataRows = cameraNumbers.map(cameraNumber => {
          const camera = camerasByNumber.get(cameraNumber) || {};
          return this.calcDefaultDataRow({
            [CAMERA_NUMBER_FIELD]: cameraNumber,
            camera_title: camera[FIELDS_CAMERA.title] || "—",
            camera_address: camera[FIELDS_CAMERA.address] || "—",
          });
        });
      } catch {
        if (version === this.requestVersion) this.hasLoadError = true;
      } finally {
        if (version === this.requestVersion) this.isLoadingTable = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.aircraft-report-cameras-dialog {
  width: min(900px, calc(100vw - 80px));
  max-height: calc(100vh - 160px);
  overflow: auto;

  .buttons-group {
    flex-wrap: wrap;
  }
}
</style>
